import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, LayoutGridIcon, ListIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
const CATEGORIES = ['Sales intelligence', 'Product updates', 'API & integrations', 'Customer stories', 'Local business data', 'Openclaw for sales'];
const CATEGORY_STYLES: Record<string, string> = {
  'Sales intelligence': 'bg-blue-50 text-blue-700',
  'Product updates': 'bg-green-50 text-green-700',
  'API & integrations': 'bg-purple-50 text-purple-700',
  'Customer stories': 'bg-amber-50 text-amber-700',
  'Local business data': 'bg-cyan-50 text-cyan-700',
  'Openclaw for sales': 'bg-rose-50 text-rose-700'
};
const COVER_IMAGES = ['https://cdn.magicpatterns.com/uploads/6NmmoxfkR8JKjQw9Ne5VQB/Concepts.png', 'https://cdn.magicpatterns.com/uploads/oFdvqgJyqwqF2M1tB7oZZ5/Input-1.png', 'https://cdn.magicpatterns.com/uploads/afH2mRVBrJjj1RhLCnEgjS/Input-2.png', 'https://cdn.magicpatterns.com/uploads/73wbVpV3vtnsgk8e1cF9Y2/Input.png'];
const FEATURED_POSTS = [{
  id: 0,
  title: 'Stop calling random zips: Territory prioritization for smarter local sales',
  excerpt: 'A data-driven framework for identifying and prioritizing the highest-value territories in your local sales strategy.',
  date: 'March 12, 2026',
  category: 'Sales intelligence',
  readTime: '8 min read',
  cover: COVER_IMAGES[0],
  href: '#/blog/territory-prioritization'
}, {
  id: 2,
  title: 'Introducing Openclaw for sales: Your AI prospecting agent',
  excerpt: 'Meet the AI agent that handles your entire prospecting workflow through natural conversation.',
  date: 'April 8, 2026',
  category: 'Product updates',
  readTime: '4 min read',
  cover: COVER_IMAGES[1]
}, {
  id: 3,
  title: 'Why traditional Google Maps scraping is dead (and what to do instead)',
  excerpt: 'Google Maps scraping used to be the go-to method. Here is why it no longer works and what modern teams are doing instead.',
  date: 'March 28, 2026',
  category: 'Local business data',
  readTime: '8 min read',
  cover: COVER_IMAGES[2]
}, {
  id: 4,
  title: 'How Clipboard Health scaled to 100k+ local healthcare facilities',
  excerpt: 'A deep dive into how Clipboard Health used Openmart data to rapidly expand their facility coverage nationwide.',
  date: 'March 15, 2026',
  category: 'Customer stories',
  readTime: '5 min read',
  cover: COVER_IMAGES[3]
}, {
  id: 5,
  title: 'The ultimate guide to B2B email sequencing for brick-and-mortar',
  excerpt: 'Everything you need to know about building email sequences that get responses from local business owners.',
  date: 'March 2, 2026',
  category: 'Sales intelligence',
  readTime: '10 min read',
  cover: COVER_IMAGES[0]
}];
const ALL_POSTS = [{
  id: 6,
  title: 'Enriching your Salesforce CRM with real-time local business data',
  excerpt: 'Learn how to automatically keep your CRM records up-to-date with the latest firmographic data, owner contacts, and tech stack signals using the Openmart API.',
  date: 'February 24, 2026',
  category: 'API & integrations',
  readTime: '7 min read',
  cover: COVER_IMAGES[1]
}, {
  id: 7,
  title: '5 cold email templates that actually work for restaurant owners',
  excerpt: 'Restaurant owners are notoriously hard to reach. Here are the exact email templates and sequencing strategies that top food-tech sales teams use to book meetings.',
  date: 'February 18, 2026',
  category: 'Sales intelligence',
  readTime: '5 min read',
  cover: COVER_IMAGES[2]
}, {
  id: 8,
  title: 'New feature: Advanced intent signals for local businesses',
  excerpt: 'We just rolled out new intent signals including recent hiring, location expansion, and technology adoption to help you prioritize your outreach.',
  date: 'February 10, 2026',
  category: 'Product updates',
  readTime: '3 min read',
  cover: COVER_IMAGES[3]
}, {
  id: 9,
  title: 'How to bypass gatekeepers when selling to dental practices',
  excerpt: 'A tactical guide to finding the actual decision-makers and practice owners, rather than getting stuck with the front desk receptionist.',
  date: 'January 29, 2026',
  category: 'Sales intelligence',
  readTime: '6 min read',
  cover: COVER_IMAGES[0]
}, {
  id: 10,
  title: 'Building a custom lead scoring model with Openmart data',
  excerpt: 'Step-by-step tutorial on using our 50+ data points to build a lead scoring model that accurately predicts which local businesses are ready to buy.',
  date: 'January 15, 2026',
  category: 'Local business data',
  readTime: '9 min read',
  cover: COVER_IMAGES[1]
}, {
  id: 11,
  title: 'Case study: Doubling ROAS with hyper-local custom audiences',
  excerpt: 'How a regional marketing agency used Openmart to build highly targeted Facebook and LinkedIn audiences, resulting in a 2.4x increase in return on ad spend.',
  date: 'January 5, 2026',
  category: 'Customer stories',
  readTime: '4 min read',
  cover: COVER_IMAGES[2]
}, {
  id: 12,
  title: 'Connecting Openmart to Clay for automated outbound',
  excerpt: 'A complete walkthrough of setting up the Openmart + Clay integration to automate your entire top-of-funnel research and personalization workflow.',
  date: 'December 12, 2025',
  category: 'API & integrations',
  readTime: '8 min read',
  cover: COVER_IMAGES[3]
}, {
  id: 13,
  title: 'The state of local business data quality in 2026',
  excerpt: 'We analyzed over 200 million local business records to understand the decay rate of contact information and why real-time verification is critical.',
  date: 'November 28, 2025',
  category: 'Local business data',
  readTime: '12 min read',
  cover: COVER_IMAGES[0]
}, {
  id: 14,
  title: 'Openclaw for sales: Best practices for prompt engineering',
  excerpt: 'Get better results from your AI sales agent by learning how to structure your natural language queries for maximum precision and relevance.',
  date: 'November 15, 2025',
  category: 'Openclaw for sales',
  readTime: '6 min read',
  cover: COVER_IMAGES[3]
}];
export function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const filteredPosts = ALL_POSTS.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    return matchesSearch && matchesCategory;
  });
  return <div className="min-w-full bg-slate-50/50 ecom-aeonik flex flex-col min-h-screen" style={{
    fontFamily: 'Aeonik, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }} data-id="element-6343">
      <Navbar data-id="element-6344" />

      <main className="flex-grow pt-12 pb-24 space-y-12 md:space-y-24" data-id="element-6345">
        {/* HERO SECTION */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" data-id="element-6346">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 pt-14 pb-14 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden" data-id="element-6347">
            {/* Subtle background blob */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-50" data-id="element-6348">
              <motion.div animate={{
              x: [0, 60, -40, 0],
              y: [0, -40, 30, 0],
              scale: [1, 1.2, 0.85, 1]
            }} transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-[-10%] left-[20%] w-[60%] h-[60%] rounded-full bg-blue-200 blur-[100px]" data-id="element-6349" />
            </div>

            {/* Subtle dot pattern overlay */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle, #156ef6 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} data-id="element-6350" />

            <div className="relative z-10 max-w-4xl mx-auto space-y-5" data-id="element-6351">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1,
              y: [0, -6, 0]
            }} transition={{
              opacity: {
                duration: 0.4,
                ease: 'easeOut'
              },
              scale: {
                duration: 0.4,
                ease: 'easeOut'
              },
              y: {
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 backdrop-blur-md text-blue-600 rounded-full text-sm font-semibold shadow-sm border border-slate-200/60" data-id="element-6352">
                <SparklesIcon className="w-4 h-4" data-id="element-6353" />
                Openmart blog
              </motion.div>

              <div className="text-4xl md:text-5xl tracking-tight leading-[1.1] font-bold" data-id="element-6354">
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.5,
                delay: 0.1,
                ease: 'easeOut'
              }} className="text-slate-900" data-id="element-6355">
                  Insights for
                </motion.span>{' '}
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.5,
                delay: 0.2,
                ease: 'easeOut'
              }} className="text-blue-600" data-id="element-6356">
                  sales
                </motion.span>
              </div>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4,
              ease: 'easeOut'
            }} className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed" data-id="element-6357">
                Product updates, sales strategies, and best practices for teams
                selling to local businesses.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.5,
              ease: 'easeOut'
            }} className="flex justify-center pt-1" data-id="element-6358">
                <Button href="http://app.openmart.com/register" variant="primary" size="lg" arrow data-id="element-6359">
                  Try Openmart free
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FEATURED POSTS (BENTO GRID) */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6360">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-14 relative overflow-hidden" data-id="element-6361">
            {/* Ambient floating blob */}
            <motion.div animate={{
            x: [0, -35, 25, 0],
            y: [0, 20, -30, 0],
            scale: [1, 1.2, 0.9, 1]
          }} transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute bottom-[-15%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-100 blur-[90px] opacity-40 pointer-events-none" data-id="element-6362" />

            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 relative z-10" data-id="element-6363">
              Featured <span className="text-blue-600" data-id="element-6364">posts</span>
            </motion.h2>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10" data-id="element-6365">
              {/* Large Featured Post */}
              <motion.a href={FEATURED_POSTS[0].href || '#'} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="lg:col-span-7 group flex flex-col bg-white rounded-3xl border border-slate-200 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-600/10 hover:border-blue-200 transition-all duration-300 overflow-hidden" data-id="element-6366">
                <div className="w-full aspect-[16/9] overflow-hidden rounded-t-3xl" data-id="element-6367">
                  <img src={FEATURED_POSTS[0].cover} alt={FEATURED_POSTS[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-6368" />
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-grow" data-id="element-6369">
                  <span className={`inline-block self-start px-3 py-1.5 rounded-lg text-xs font-semibold mb-5 ${CATEGORY_STYLES[FEATURED_POSTS[0].category]}`} data-id="element-6370">
                    {FEATURED_POSTS[0].category}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors leading-snug" data-id="element-6371">
                    {FEATURED_POSTS[0].title}
                  </h3>
                  <p className="text-slate-500 text-base leading-relaxed mb-6 line-clamp-2" data-id="element-6372">
                    {FEATURED_POSTS[0].excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-slate-400 font-medium mt-auto pt-4 border-t border-slate-100" data-id="element-6373">
                    <span data-id="element-6374">{FEATURED_POSTS[0].date}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-6375" />
                    <span data-id="element-6376">{FEATURED_POSTS[0].readTime}</span>
                    <span className="ml-auto flex items-center text-sm font-semibold text-blue-500 group-hover:text-blue-600 transition-colors" data-id="element-6377">
                      Read article
                      <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" data-id="element-6378" />
                    </span>
                  </div>
                </div>
              </motion.a>

              {/* Smaller Featured Posts */}
              <div className="lg:col-span-5 flex flex-col gap-8" data-id="element-6379">
                {FEATURED_POSTS.slice(1, 4).map((post, index) => <motion.a key={post.id} href="#" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: 0.2 + index * 0.1
              }} className="flex-1 group flex flex-row bg-white rounded-2xl border border-slate-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-600/10 hover:border-blue-200 transition-all duration-300 overflow-hidden" data-id="element-6380">
                    <div className="w-[140px] md:w-[180px] shrink-0 overflow-hidden" data-id="element-6381">
                      <img src={post.cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-6382" />
                    </div>
                    <div className="p-6 flex flex-col justify-center flex-grow min-w-0" data-id="element-6383">
                      <span className={`inline-block self-start px-2.5 py-1 rounded-md text-[11px] font-semibold mb-3 ${CATEGORY_STYLES[post.category]}`} data-id="element-6384">
                        {post.category}
                      </span>
                      <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 leading-snug" data-id="element-6385">
                        {post.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-slate-400 font-medium mt-auto" data-id="element-6386">
                        <span data-id="element-6387">{post.date}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-6388" />
                        <span data-id="element-6389">{post.readTime}</span>
                      </div>
                    </div>
                  </motion.a>)}
              </div>
            </div>
          </div>
        </section>

        {/* ALL POSTS GRID */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6390">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-14 relative overflow-hidden" data-id="element-6391">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-10" data-id="element-6392">
              Latest <span className="text-blue-600" data-id="element-6393">articles</span>
            </motion.h2>

            {/* Controls */}
            <div className="flex flex-col gap-6 mb-12 pb-8 border-b border-slate-100" data-id="element-6394">
              <div className="flex flex-wrap gap-2.5" data-id="element-6395">
                <button onClick={() => setActiveCategory('All')} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${activeCategory === 'All' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`} data-id="element-6396">
                  All
                </button>
                {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${activeCategory === cat ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'}`} data-id="element-6397">
                    {cat}
                  </button>)}
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-6398">
                <div className="relative flex-1 max-w-md" data-id="element-6399">
                  <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-6400" />
                  <input type="text" placeholder="Search posts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" data-id="element-6401" />
                </div>
                <div className="flex items-center bg-slate-50 p-1.5 rounded-xl border border-slate-200 self-start md:self-auto" data-id="element-6402">
                  <button onClick={() => setViewMode('grid')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'grid' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6403">
                    <LayoutGridIcon className="w-4 h-4" data-id="element-6404" />
                    Grid
                  </button>
                  <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6405">
                    <ListIcon className="w-4 h-4" data-id="element-6406" />
                    List
                  </button>
                </div>
              </div>
            </div>

            {/* Posts Grid/List */}
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' : 'flex flex-col gap-8 max-w-4xl mx-auto'} data-id="element-6407">
              {filteredPosts.map((post, index) => <motion.a href="#" key={post.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.06
            }} className={`group flex bg-white rounded-2xl border border-slate-200 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-600/10 hover:border-blue-200 transition-all duration-300 overflow-hidden ${viewMode === 'list' ? 'flex-row items-stretch' : 'flex-col'}`} data-id="element-6408">
                  {/* Cover Image */}
                  {viewMode === 'list' ? <div className="w-[200px] md:w-[260px] shrink-0 overflow-hidden" data-id="element-6409">
                      <img src={post.cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-6410" />
                    </div> : <div className="w-full aspect-[16/10] overflow-hidden" data-id="element-6411">
                      <img src={post.cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-6412" />
                    </div>}

                  <div className="p-8 flex flex-col flex-grow" data-id="element-6413">
                    <div className="mb-4" data-id="element-6414">
                      <span className={`inline-block px-3 py-1.5 rounded-lg text-xs font-semibold ${CATEGORY_STYLES[post.category]}`} data-id="element-6415">
                        {post.category}
                      </span>
                    </div>

                    <h3 className={`font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug ${viewMode === 'list' ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'}`} data-id="element-6416">
                      {post.title}
                    </h3>

                    <div className="flex items-center gap-3 text-xs text-slate-400 font-medium mt-auto border-t border-slate-100 pt-5" data-id="element-6417">
                      <span data-id="element-6418">{post.date}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-6419" />
                      <span data-id="element-6420">{post.readTime}</span>
                      <span className="ml-auto flex items-center text-sm font-semibold text-blue-500 group-hover:text-blue-600 transition-colors" data-id="element-6421">
                        Read more
                        <ArrowRightIcon className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-6422" />
                      </span>
                    </div>
                  </div>
                </motion.a>)}
            </div>

            {filteredPosts.length === 0 && <div className="text-center py-24" data-id="element-6423">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100" data-id="element-6424">
                  <SearchIcon className="w-6 h-6 text-slate-400" data-id="element-6425" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6426">
                  No posts found
                </h3>
                <p className="text-slate-500" data-id="element-6427">
                  Try adjusting your search terms or category filter.
                </p>
              </div>}
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6428">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-[2.5rem] py-20 md:py-24 px-4 sm:px-6 lg:px-8 text-center shadow-xl relative overflow-hidden" data-id="element-6429">
            <motion.div animate={{
            x: [0, 60, -50, 0],
            y: [0, -50, 35, 0],
            scale: [1, 1.3, 0.85, 1]
          }} transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500 blur-[80px] opacity-40 pointer-events-none" data-id="element-6430" />
            <motion.div animate={{
            x: [0, -50, 35, 0],
            y: [0, 35, -40, 0],
            scale: [1, 0.85, 1.25, 1]
          }} transition={{
            duration: 13,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-400 blur-[60px] opacity-35 pointer-events-none" data-id="element-6431" />
            <h2 className="text-3xl md:text-4xl text-white mb-8 max-w-3xl mx-auto font-bold relative z-10" data-id="element-6432">
              Start reaching local businesses today
            </h2>
            <div className="max-w-md mx-auto relative z-10" data-id="element-6433">
              <div className="mb-8" data-id="element-6434">
                <Button href="http://app.openmart.com/register" variant="secondary" size="lg" fullWidth arrow data-id="element-6435">
                  Try verified contacts for free
                </Button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm font-medium text-white/90" data-id="element-6436">
                <div className="flex items-center gap-2" data-id="element-6437">
                  <div className="w-4 h-4 rounded-full bg-blue-500/30 flex items-center justify-center" data-id="element-6438">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-6439" />
                  </div>
                  No credit card required
                </div>
                <div className="flex items-center gap-2" data-id="element-6440">
                  <div className="w-4 h-4 rounded-full bg-blue-500/30 flex items-center justify-center" data-id="element-6441">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-6442" />
                  </div>
                  100 free verified contacts
                </div>
                <div className="flex items-center gap-2" data-id="element-6443">
                  <div className="w-4 h-4 rounded-full bg-blue-500/30 flex items-center justify-center" data-id="element-6444">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-6445" />
                  </div>
                  Export to CSV/CRM
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-6446" />
    </div>;
}